import dayjs from 'dayjs'
import { compact, isEmpty } from 'lodash'
import moment from 'moment'

import httpClient, { catchError } from '@emmacare/common/api/httpClient'
import safeJSONParse from '@emmacare/common/utils/safeJSONParse'

import {
  COMPANY_DIVISION,
  DIVISION,
  EXPORT_INFO_MESSAGE,
  FILE_NAME_REGEXP,
  HTML_TAGS_REGEXP,
  HTTP_ACCEPTED,
  HTTP_UNPROCESSABLE,
  NEXT_CALL_DATE_ERROR_MESSAGE,
  UUID,
  VALUE_IN_RANGE_ERROR_MESSAGE
} from 'src/constants'
import { showInfoModal, showWarningModal } from 'src/services/dialogs'
import { showSuccessModal } from 'src/services/dialogs/v5'
import { findItemByField } from 'src/utils/Array'
import { replaceKeyWords } from 'src/utils/String'

export const getFileName = (content = '', fileNameDefault = '') => {
  const fileName = content.replace(FILE_NAME_REGEXP, '')

  return fileName || fileNameDefault
}

/**
 * Handles export response: 202 with message or blob download.
 * Use after a download request (e.g. from useExportPatientList).
 * @returns {Promise<boolean>} true when handled
 */
export const processExportResponse = async (
  response = {},
  fileNameDefault = '',
  successMessage = ''
) => {
  const { data, headers = {}, status } = response

  if (status === HTTP_ACCEPTED) {
    const content = safeJSONParse(await data.text())?.message || successMessage

    showSuccessModal({ content })
    return true
  }

  const content = headers['content-disposition']
  const fileName = getFileName(content, fileNameDefault)
  const link = document.createElement('a')

  link.setAttribute('href', window.URL.createObjectURL(new Blob([data])))
  link.setAttribute('download', fileName)

  document.body.appendChild(link)

  link.click()
  return true
}

export const exportReport = async (
  config = {},
  fileNameDefault = '',
  successMessage = ''
) => {
  try {
    const response = await httpClient.request({
      method: 'get',
      url: config,
      isDownload: true
    })
    return await processExportResponse(
      response,
      fileNameDefault,
      successMessage
    )
  } catch (err) {
    const message = err?.message || err?.error || ''
    const status = err?.status || 0

    if (
      status === HTTP_UNPROCESSABLE ||
      message.indexOf('No records were found.') !== -1
    ) {
      showWarningModal({
        content: message
      })
    } else {
      catchError(err)
    }
  }
}

export const exportReportWithConfirm = async (
  config = {},
  fileNameDefault = ''
) => {
  showInfoModal({
    content: EXPORT_INFO_MESSAGE,
    okButtonProps: {
      className: 'confirm-download'
    },
    onOk: () => exportReport(config, fileNameDefault)
  })
}

export const isInRange =
  (min = 0, max = 0) =>
  (_, value, callback) => {
    if (+value >= +min && +value <= +max) {
      callback()
    } else {
      callback(
        replaceKeyWords(VALUE_IN_RANGE_ERROR_MESSAGE, {
          '{:min}': min,
          '{:max}': max
        })
      )
    }
  }

export const isDateAfterToday = ({ required }, value, callback) => {
  if (!required) return callback()

  return value && moment(value).isAfter(moment().endOf('day'))
    ? callback()
    : callback(NEXT_CALL_DATE_ERROR_MESSAGE)
}

export const isDateAfterTodayDayjs = ({ required }, value, callback) => {
  if (!required) return callback()

  return value && dayjs(value).isAfter(dayjs().endOf('day'))
    ? callback()
    : callback(NEXT_CALL_DATE_ERROR_MESSAGE)
}

export const checkCategory =
  (insuranceProgramType = []) =>
  ({ message = '' }, value = null) => {
    const isAllSelected = !compact(insuranceProgramType).length

    return !value && isAllSelected
      ? Promise.reject(new Error(message))
      : Promise.resolve()
  }

export const checkInsuranceProgramType =
  (category = null) =>
  ({ message = '' }, value = []) => {
    const isAllSelected = !compact(value).length

    return !category && isAllSelected
      ? Promise.reject(new Error(message))
      : Promise.resolve()
  }

export const pluralize = (count, noun, suffix = 's') =>
  `${count} ${noun}${count !== 1 ? suffix : ''}`

export const setMomentTimePickerValue = (
  date = Date.now(),
  hour = '8',
  minute = '00'
) => {
  const timedDate = date ? moment(date).toDate() : moment().toDate()

  timedDate.setHours(hour)
  timedDate.setMinutes(minute)

  return moment(timedDate)
}

export const isRequiredQuill = ({ message, required }, value, callback) => {
  if (!required) return callback()

  const checkValue = value && value.replace(HTML_TAGS_REGEXP, '')

  if (isEmpty(checkValue)) {
    callback(message)
  } else {
    callback()
  }
}

export const checkMaxLengthQuill = ({ max, message }, value, callback) => {
  const checkValue = value && value.replace(HTML_TAGS_REGEXP, '')

  if (!isEmpty(checkValue) && checkValue?.length > max) {
    callback(message)
  } else {
    callback()
  }
}

export const getInitialDivision = (
  companyHierarchy = [],
  editableItem = null
) => {
  const companyUuid =
    editableItem?.[COMPANY_DIVISION]?.[UUID] ||
    editableItem?.[DIVISION]?.[UUID] ||
    null
  const initialCompany = findItemByField({
    itemList: companyHierarchy,
    field: 'value',
    value: companyUuid
  })

  return !isEmpty(initialCompany) ? companyUuid : null
}
